import { memo, useEffect, useState, useCallback } from 'react';
import { Space, Button, Typography, App } from 'antd';
import { ExclamationCircleOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import { useRequest } from '@wappsto/blanket';
import { Trans } from 'react-i18next';
import { getRequestErrorMessage } from 'util/helpers';
import { useTranslation } from 'translations';
import GenericNotification from './GenericNotification';

const CREATE = 'create';
const DELETE = 'delete';
const DELETED = 'deleted';
const DENIED = 'denied';
const ACCEPTED = 'accepted';
const READ = 'read';

interface InstallationPermissionProps {
  notification: any;
  updateNotificationStatus: (...args: any[]) => any;
  deleteNotification: (...args: any[]) => any;
  onClick: (...args: any[]) => any;
  isDeleting: boolean;
}

const InstallationPermission = memo(
  ({ notification, updateNotificationStatus, deleteNotification, onClick, isDeleting }: InstallationPermissionProps) => {
    const { t, tC } = useTranslation('features', 'notifications.appNotifications.permissions');
    const { message } = App.useApp();
    const title = notification.base?.from_name;
    const { request, send } = useRequest();
    const [loading, setLoading] = useState(false);
    const error =
      request &&
      request.status === 'error' &&
      request.responseStatus !== 404 &&
      request.responseStatus !== 401;

    const acceptPermission = (e) => {
      e.stopPropagation();
      setLoading(true);
      send({
        method: 'GET',
        url: `/acl/${notification.base.from}/permission/${notification.base.from}`,
      });
    };

    const updateACL = useCallback(() => {
      const method: Record<string, boolean> = {};
      notification.custom.method.forEach((strName) => {
        let name = strName;
        if (name === 'add') {
          name = CREATE;
        } else if (name === 'delete_not_owned') {
          name = DELETE;
        }
        method[name] = true;
      });
      let create;
      if (method.create) {
        create = request.json.installation?.[0]?.create || [];
        method.retrieve = true;
        method.update = true;
        create.push(notification.custom.collection);
      }
      const restriction = {
        create,
        method,
      };
      send({
        method: 'PATCH',
        url: `/acl/${notification.base.from}/permission/${notification.base.from}?propagate=true`,
        body: {
          restriction: [restriction],
        },
      });
    }, [notification, request, send]);

    const denyPermission = (e) => {
      e.stopPropagation();
      updateNotificationStatus(notification, DENIED, null, true);
    };

    useEffect(() => {
      if (request) {
        if (request.status === 'success' && request.json) {
          if (request.method === 'GET') {
            updateACL();
          } else {
            setLoading(false);
            updateNotificationStatus(notification, ACCEPTED);
          }
        } else if (request.status === 'error') {
          setLoading(false);
          let errorMessage;
          const {
            meta: { id },
          } = notification;
          if (request.responseStatus === 404 || request.responseStatus === 401) {
            updateNotificationStatus(notification, DELETED);
            errorMessage = tC('itemDeleted');
          } else {
            updateNotificationStatus(notification, READ);
            const m = getRequestErrorMessage(request, tC);
            errorMessage = m.message;
          }
          message.open({
            key: `acceptError_${id}`,
            type: 'error',
            content: (
              <>
                <Trans t={t} i18nKey="acceptError" />
                <Typography.Paragraph type="danger">{errorMessage}</Typography.Paragraph>
              </>
            ),
          });
        }
      }
    }, [request, notification, updateACL, updateNotificationStatus, tC, message, t]);

    return (
      <GenericNotification
        title={title}
        description={
          <Trans
            t={t}
            i18nKey={`${notification.read === ACCEPTED ? ACCEPTED : 'description'}`}
            values={{
              name: notification.base.from_name,
              method:
                notification.custom?.method instanceof String
                  ? notification.custom?.method?.at(0)
                  : notification.custom?.method,
            }}
          />
        }
        notification={notification}
        deleteNotification={deleteNotification}
        isDeleting={isDeleting}
        onClick={onClick}
        extra={
          notification.read !== ACCEPTED &&
          notification.read !== DELETED && (
            <Space>
              {notification.read !== DENIED && (
                <Button size="small" icon={<DislikeOutlined />} onClick={denyPermission}>
                  {tC('declineButton')}
                </Button>
              )}
              <Button
                size="small"
                type="primary"
                onClick={acceptPermission}
                loading={loading}
                icon={
                  error ? <ExclamationCircleOutlined style={{ color: 'red' }} /> : <LikeOutlined />
                }>
                {tC('allowButton')}
              </Button>
            </Space>
          )
        }
      />
    );
  },
);

InstallationPermission.displayName = 'InstallationPermission';

export default InstallationPermission;
